import {
    isSafari
} from '../../../core/js/utils/browserUtils';

import {
    generate
} from '../../../core/js/utils/componentUtils';

import { getIsElementInViewport } from '../../../core/js/utils/scrollUtils.js';
import {
    handleThirdPartyVideoEmbed
} from '../../../core/js/utils/videoUtils';

import { createGAEvent } from '../../../core/js/utils/analyticsUtils';
import { handleUtagEvent } from '../../../core/js/actions/initialActions.js';
import { pauseEventListeners } from '../../../core/js/utils/eventUtils';

/**
 * This function can be used to access events fired during pause. It recieves two arguments from
 * pauseEventListeners resume method and runs custom logic on those objects.
 * @param {Object} eventsFired - An object containing events fired during pause.
 * Each key contains a value of { element, type, handler }.
 * @param {Array} eventsFiredArray - An array containing all the keys in eventsFired.
 * @returns {Object} Returns an object containing which events should be fired.
 */
function conditionallyFilterEventsFired(eventsFired, eventsFiredArray) {
    if (!eventsFired) { return; }

    let eventsToFire = {};

    for(let i = 0; i < eventsFiredArray.length; i++) {
        // prioritize fullscreenchange events over other events.
        if (eventsFiredArray[i].includes('fullscreenchange')) {
            eventsToFire[eventsFiredArray[i]] = eventsFired[eventsFiredArray[i]];
        }
    }

    return Object.keys(eventsToFire).length ? eventsToFire : eventsFired;
}

function addTrackerVideoEvent(store, tracker, event, action) {
    const hasAnalyticsTitle = !_.isUndefined($(event.target).attr('data-analytics-title'));
    if (hasAnalyticsTitle) {
        const videoTitle = $(event.target).attr('data-analytics-title');
        const eventCategory = $(event.target).attr('data-analytics-category') ||
            _.get(store.getState(), 'category.categorySlug');

        tracker.addEvent(event, () => {
            fireGAEvent(store, eventCategory, `video|${action}`, videoTitle);
        });
    }
}

function attachEvents(store) {
    /*
    * Doc ready is for videos that autoplay. If it's not wrapped
    * the video autoplays before the play event is bound and it
    * doesn't get tracked.
    */
    $(document).ready(function onReady() {
        const PAUSE_LISTENER_TIMEOUT = 500;
        let tracker = pauseEventListeners(conditionallyFilterEventsFired, PAUSE_LISTENER_TIMEOUT);

        document.addEventListener('resume-listening', (e) => {
            const eventsToFire = _.get(e, 'detail.eventsToFire', {});
            const eventsToFireArray = _.get(e, 'detail.eventsToFireArray', []);

            for(let i = 0; i < eventsToFireArray.length; i++) {
                eventsToFire[eventsToFireArray[i]].handler();
            }
            tracker = tracker.removeEvents();
        });

        // We must attach these to document, and pass true as 3rd argument, so they bubble up from the video element
        document.addEventListener('play', (e) => {
            const isAutoPlayVideo = _.get(e.target.attributes, 'autoplay.specified', false);
            if (!isAutoPlayVideo) {
                addTrackerVideoEvent(store, tracker, e, 'play');
            }
        }, true);

        document.addEventListener('pause', (e) => {
            let isAutoPlayVideo = _.get(e.target.attributes, 'autoplay.specified', false);
            const hasFinishedPlaying = e.target.currentTime === e.target.duration;

            if (isAutoPlayVideo && getIsElementInViewport(e.target)) {
                e.target.removeAttribute('autoplay');
                isAutoPlayVideo = _.get(e.target.attributes, 'autoplay.specified', false);
            }

            if (!isAutoPlayVideo && !hasFinishedPlaying) {
                addTrackerVideoEvent(store, tracker, e, 'pause');
            }
        }, true);

        $(document).on('webkitfullscreenchange mozfullscreenchange msfullscreenchange fullscreenchange', (e) => {
            // Distinguish event between fullscreen entry or exit. Do not fire on exit.
            // Note: "fullscreenElement" is not supported by Safari, must check for "webkitCurrentFullScreenElement"
            const isFullScreen = !_.isNil(document.webkitCurrentFullScreenElement) ||
                                 !_.isNil(document.fullscreenElement);
            if (isFullScreen) {
                addTrackerVideoEvent(store, tracker, e, 'full_screen');
            }
        });

        // Adding click event to video element to pause and play the video.
        // On Safari, video.paused is not implemented and the click behavior
        // is already default, so don't set these events for Safari
        if (!isSafari(navigator.userAgent)) {
            $('video').on('click', function clickEvent() {
                if (this.currentTime > 0 && !this.paused && !this.ended) {
                    this.pause();
                } else {
                    this.removeAttribute('muted');
                    this.play();
                }
            });
        }

        // iframes are the work of the devil and do not inherently play nicely with
        // responsive web design so we need to manipulate their heights manually
        const $allVideos = $("iframe[src*='//player.vimeo.com'], iframe[src*='//www.youtube.com'], object, embed");

        if ($allVideos) {
            $(window).resize(() => {
                $allVideos.each(function thirdPartyEmbed() {
                    handleThirdPartyVideoEmbed(this);
                });
            })
            .resize();
        }
    });
}

/**
 * Quick util function to handle sending off a GA event
 * Just to keep code DRYer in this file
 * @param  {String} store       the store
 * @param  {String} slug        category slug, first param for GA event
 * @param  {String} eventType   the specific kind of event that happened, second param for GA event
 * @param  {String} title       title of the video, third param for GA event
 * @return {undefined}
 */
function fireGAEvent(store, slug, eventType, title) {
    const gaEvent = createGAEvent(slug, eventType, title);
    store.dispatch(handleUtagEvent(gaEvent, 'link'));
}

/**
 *
 * Users of this class can overwrite the tagBaseSlug
 *  to use a different base slug on the tag
 *
 * @param {object} store The redux store
 * @param {object} overrides Params to override componentUtils defaults
 * @returns {*} Component generator
 */
export function init(store, overrides = {}) {
    const config = {
        name: 'videoComponent',
        target: '.js-video',
        attachEvents: attachEvents
    };

    return generate(config, store, overrides);
}
